<script setup lang="ts">
import DragPan from "ol/interaction/DragPan";
import Projection from "ol/proj/Projection";
import { Icon, Style } from "ol/style";
// @ts-nocheck
import { nextTick, onMounted, ref, toRef, watch } from "vue";
import {
  Interactions as _Interactions,
  Layers as _Layers,
  Map as _Map,
  MapControls as _MapControls,
  Sources as _Sources,
  Styles as _Styles,
} from "vue3-openlayers";
import type { Extent } from "ol/extent";
import "ol/ol.css";

/**
 * openMap —— 重新实现的楼层图组件（替代丢失源码的 gkestor-battlefield-cesium-plus）
 *
 * 通过 vue3-openlayers 渲染一张静态楼层影像，并在其上叠加矢量图层，
 * 支持绘制（区域/逃生路线）、编辑、吸附、变换等交互。
 *
 * 关键点：
 * 1. 地图坐标系使用「像素」单位，坐标与图片像素一一对应，
 *    因此内部始终使用自建的像素投影（xkcd-image / pixels），
 *    忽略外部传入的 projection（如 EPSG:3857），否则楼层图坐标会错乱。
 * 2. center / zoom / size / extent / imgUrl 均与父组件双向联动，
 *    图片切换时会自动重设视图。
 */
const props = defineProps({
  apiKey: { type: String, default: "" },
  // 外部传入的中心点。图片切换后组件会以图片中心重置视图；外部显式修改 center 时立即生效
  center: { type: Array, default: () => [0, 0] },
  // 外部传入的缩放级别。首次加载/切换图片时组件会按图片 extent 自动适配（fit），
  // 之后外部显式修改 zoom 时立即生效并覆盖自动适配结果
  zoom: { type: Number, default: 1 },
  // 外部传入的投影（保留接口兼容，实际不使用，改用内部像素投影）
  projection: { type: [String, Object], default: undefined },
  imgUrl: { type: String, default: "" },
  // 图片的像素尺寸 [width, height]，与 extent（图片的坐标范围）配套使用。
  // 原生 ol/source/ImageStatic 用它定义图片渲染分辨率；当 extent 宽高比与图片实际
  // 像素宽高比不一致时，该值可作图片真实像素尺寸用于拉伸矫正。
  // 注意：vue3-openlayers 的 ol-source-image-static 未暴露 imageSize 属性，
  // 实际渲染范围由 extent 决定，size 主要作为元数据保留（与父组件同步维护）
  size: { type: Array, default: () => [1024, 968] },
  extent: { type: Array, default: () => [0, 0, 1024, 968] },
  configMenuItems: { type: Array, default: () => [] },
  activatedBottomBar: { type: String, default: "" },
  waring: { type: [String, Number, null] as any, default: null },
  activatedPositionOverlayWaring: { type: Array, default: () => [] },
  activatedPositionOverlay: { type: Array, default: () => [] },
  activatedPropertiesOverlay: { type: Object, default: () => ({}) },
  listTitle: { type: String, default: "" },
  alarmType: { type: String, default: "" },
  svgOverlay: { type: String, default: "" },
  waringIcon: { type: String, default: "" },
  fnUpdateDrawFeature: { type: Function, default: undefined },
  fnUpdateDrawFeature2: { type: Function, default: undefined },
  fnOverrideStyle: { type: Function, default: undefined },
});
const emit = defineEmits([
  "ready",
  "featuresChanged",
  "customPointerMove",
  "customContextMenu",
  "customPostCompose",
]);
// 组件擦除为 any：避免具体 ol 组件类型进入 _sfc_main 返回类型触发 TS2742/TS7056
const OlMap: any = _Map.OlMap;
const OlOverlay: any = _Map.OlOverlay;
const OlView: any = _Map.OlView;
const OlImageLayer: any = _Layers.OlImageLayer;
const OlVectorLayer: any = _Layers.OlVectorLayer;
const OlSourceImageStatic: any = _Sources.OlSourceImageStatic;
const OlSourceVector: any = _Sources.OlSourceVector;
const OlContextMenuControl: any = _MapControls.OlContextMenuControl;
const OlZoomControl: any = _MapControls.OlZoomControl;
const OlDrawInteraction: any = _Interactions.OlDrawInteraction;
const OlModifyInteraction: any = _Interactions.OlModifyInteraction;
const OlSnapInteraction: any = _Interactions.OlSnapInteraction;
const OlTransformInteraction: any = _Interactions.OlTransformInteraction;
const OlStyle: any = _Styles.OlStyle;

const mapRef = ref<any>(null);
const viewRef = ref<any>(null);
const vectorSourceRef = ref<any>(null);
const currentImageUrl = toRef(props, "imgUrl");

// 内部像素投影：使地图坐标与图片像素一一对应
const pixelProjection: any = new Projection({
  code: "xkcd-image",
  units: "pixels",
  extent: props.extent as unknown as Extent,
});

// 图片尺寸变化时，同步投影的 extent
watch(
  () => props.extent,
  (extent) => {
    if (extent && pixelProjection) {
      pixelProjection.setExtent(extent as unknown as Extent);
    }
    // 图片范围更新后重新适配视图，确保图片按新尺寸正确显示（无需二次操作）
    nextTick(() => {
      // eslint-disable-next-line no-use-before-define
      calculateAutoZoom();
    });
  },
);

// 外部传入的 zoom 变化时直接同步到视图，保证外部控制立即生效
watch(
  () => props.zoom,
  (val) => {
    const view = viewRef.value?.view;
    if (view && typeof val === "number" && !Number.isNaN(val)) {
      view.setZoom(val);
    }
  },
);

// 外部传入的 center 变化时直接同步到视图，保证外部控制立即生效
watch(
  () => props.center,
  (val) => {
    const view = viewRef.value?.view;
    if (view && Array.isArray(val) && val.length === 2) {
      view.setCenter(val);
    }
  },
);

const getVectorSource = () => vectorSourceRef.value?.source;
const getMap = () => mapRef.value?.map;
const getView = () => viewRef.value?.view;

// 状态图标处理缓存：key = `${src}#${mode}`，value = 处理后图片的 dataURL。
// 同一张图片的同一状态只处理一次，后续 createIcon 直接读缓存，避免重复做像素级转换。
// 仅放内存、不写 localStorage：图标源有限且体积小（几十 KB/张），会话级缓存完全无压力；
// localStorage 有 5MB 配额、base64 膨胀约 37%、且同步 I/O 阻塞主线程，没有必要持久化。
const iconColorCache = new Map<string, string>();
// 正在处理的缓存 key，防止同一 src+状态被并发重复触发
const iconColorPending = new Set<string>();

// 着色目标色：0 故障=橙、1 报警=红
const STATE_COLORS: Record<string, { r: number; g: number; b: number }> = {
  orange: { r: 255, g: 140, b: 0 },
  red: { r: 230, g: 30, b: 30 },
};
// 设备状态 → 处理模式：0 故障=orange、1 报警=red、4 离线=gray；
// 2（正常）及其它（-1 未启用/缺失）返回 null，直接使用原图，不产生新图与缓存。
const getIconMode = (state: unknown): string | null => {
  const s = Number(state);
  if (s === 0) return "orange";
  if (s === 1) return "red";
  if (s === 4) return "gray";
  return null;
};

// 同步创建设备图标样式（style 函数必须同步 return）：
// 1) 状态为 0/1/4：优先使用按状态处理后的缓存图；缓存未就绪时先用原图占位，
//    同时异步处理，完成后写缓存并刷新当前图层要素使图标更新；
// 2) 状态为 2（正常）及其它：直接使用原图 src，不做任何处理。
const createIcon = (src: string, scale: number, state?: unknown): any => {
  const mode = getIconMode(state);
  let effectiveSrc = src;
  if (mode) {
    const key = `${src}#${mode}`;
    effectiveSrc = iconColorCache.get(key) || src;
    if (!iconColorCache.has(key) && !iconColorPending.has(key)) {
      iconColorPending.add(key);
      processIcon(key, src, mode, (result: string) => {
        iconColorCache.set(key, result);
        iconColorPending.delete(key);
        // eslint-disable-next-line no-use-before-define
        refreshFeaturesUsingIcon();
      });
    }
  }
  return new Style({
    image: new Icon({
      src: effectiveSrc,
      anchor: [0.5, 1],
      anchorXUnits: "fraction",
      anchorYUnits: "fraction",
      scale,
    }),
  });
};

// 某张按状态处理后的图片就绪后，重设当前图层所有设备要素的样式，
// 使仍使用原图占位的图标切换到处理后的状态图。
// 依赖 index.vue 传入的 fnOverrideStyle（其内部会再次调用 createIcon 读取缓存）。
const refreshFeaturesUsingIcon = (): void => {
  const source = vectorSourceRef.value?.source;
  const fn = props.fnOverrideStyle;
  if (!source || typeof fn !== "function") return;
  source.getFeatures().forEach((feature: any) => {
    if (feature.get("code")) {
      fn(feature);
    }
  });
};

// 将图片异步处理为 dataURL：mode=gray 灰度化；mode=orange/red 按目标色着色。
// 着色保留原图亮度层次（亮部更接近目标色、暗部更暗），透明区域保持不变。
// 失败（如跨域导致 canvas 被污染）时仅移除 pending，回退继续使用原图。
function processIcon(
  key: string,
  imageSrc: string,
  mode: string,
  callback: (dataUrl: string) => void,
): void {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      // 1. 绘制原图
      ctx.drawImage(img, 0, 0);
      // 2. 获取像素数据，按状态处理（透明度通道保持不变）
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const tint = STATE_COLORS[mode];
      for (let i = 0; i < data.length; i += 4) {
        const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        if (tint) {
          // 着色：用亮度调制目标色，保留图标明暗层次
          const factor = 0.4 + 0.6 * (lum / 255);
          data[i] = tint.r * factor;
          data[i + 1] = tint.g * factor;
          data[i + 2] = tint.b * factor;
        } else {
          // 离线：标准灰度
          data[i] = lum;
          data[i + 1] = lum;
          data[i + 2] = lum;
        }
      }
      // 3. 写回 canvas 并导出 dataURL
      ctx.putImageData(imageData, 0, 0);
      callback(canvas.toDataURL("image/png"));
    } catch (error) {
      iconColorPending.delete(key);
    }
  };
  img.onerror = () => {
    iconColorPending.delete(key);
  };
  img.src = imageSrc;
}

const clearSource = () => vectorSourceRef.value?.source?.clear();
const addFeature = (feature: any) =>
  vectorSourceRef.value?.source?.addFeature(feature);
const addFeatures = (features: any[]) =>
  vectorSourceRef.value?.source?.addFeatures(features);
const removeFeature = (feature: any) =>
  vectorSourceRef.value?.source?.removeFeature(feature);
const getFeatures = () =>
  vectorSourceRef.value?.source
    ? vectorSourceRef.value.source.getFeatures()
    : [];
const getZoom = () => mapRef.value?.map?.getView()?.getZoom();
const setZoom = (zoom: number) => mapRef.value?.map?.getView()?.setZoom(zoom);

const calculateAutoZoom = () => {
  const container = document.getElementById("ypc-map-container");
  if (!container) return;
  const view = viewRef.value?.view;
  if (view && typeof view.fit === "function") {
    view.fit(props.extent as unknown as Extent, {
      size: [container.clientWidth, container.clientHeight],
      padding: [10, 10, 10, 10],
      constrainResolution: false,
      nearest: false,
    });
  }
};

// 绘制模式下，地图拖拽平移（DragPan）会与绘制交互冲突，
// 导致按住鼠标拖动放置顶点时底图跟着平移，因此绘制期间禁用 DragPan。
const isDrawingMode = (): boolean =>
  props.activatedBottomBar === "区域绘制" ||
  props.activatedBottomBar === "逃生路线";

const setDragPanActive = (active: boolean): void => {
  const map = mapRef.value?.map;
  if (!map) return;
  map
    .getInteractions()
    .getArray()
    .forEach((interaction: any) => {
      if (interaction instanceof DragPan) {
        interaction.setActive(active);
      }
    });
};

// 切换底部栏按钮时，同步 DragPan 的启用/禁用状态
watch(
  () => props.activatedBottomBar,
  () => {
    setDragPanActive(!isDrawingMode());
  },
);

defineExpose({
  getVectorSource,
  getMap,
  getView,
  createIcon,
  clearSource,
  addFeature,
  addFeatures,
  removeFeature,
  getFeatures,
  getZoom,
  setZoom,
  calculateAutoZoom,
});

onMounted(() => {
  nextTick(() => {
    emit("ready");
    const map = mapRef.value?.map;
    if (map) {
      map.on("postcompose", (e: any) => emit("customPostCompose", e));
      map.on("pointermove", (e: any) => emit("customPointerMove", e));
      map.on("contextmenu", (e: any) => emit("customContextMenu", e));
      setDragPanActive(!isDrawingMode());
    }
  });
});
</script>

<template>
  <OlMap ref="mapRef" class="w-full" style="height: 100%">
    <OlView
      ref="viewRef"
      :center="center"
      :zoom="zoom"
      :projection="pixelProjection"
    />

    <OlZoomControl />

    <OlContextMenuControl :items="configMenuItems" />

    <!-- 底图静态影像层：key 绑定 imgUrl+extent，二者任一变化即整体销毁重建影像层。
         因为 OL ImageStatic 的 imageExtent 在构造后不可变，vue3-openlayers 只能靠重建 source
         来应用新 extent；若不加 key，重建/图片异步加载期间会短暂以旧 imageExtent 渲染，
         导致首帧图标与底图错位（再次点击楼层后才对齐） -->
    <OlImageLayer
      id="xkcd"
      :key="`${currentImageUrl}|${extent.join(',')}`"
      :z-index="-1"
    >
      <OlSourceImageStatic
        :url="currentImageUrl"
        :image-extent="extent"
        :projection="pixelProjection"
      />
    </OlImageLayer>

    <!-- 矢量图层 -->
    <OlVectorLayer :z-index="1">
      <OlSourceVector ref="vectorSourceRef">
        <!-- 区域设置 -->
        <OlModifyInteraction v-if="activatedBottomBar === '区域设置'" />
        <!-- 区域绘制 -->
        <OlDrawInteraction
          v-if="activatedBottomBar === '区域绘制'"
          type="Polygon"
          :stop-click="true"
          @drawend="fnUpdateDrawFeature"
        />
        <!-- 逃生路线 -->
        <OlDrawInteraction
          v-if="activatedBottomBar === '逃生路线'"
          ref="drawRef"
          type="LineString"
          :stop-click="true"
          @drawend="fnUpdateDrawFeature2"
        />
        <!-- 区域设置吸附 -->
        <OlSnapInteraction v-if="activatedBottomBar === '区域设置'" />
      </OlSourceVector>

      <OlStyle :override-style-function="fnOverrideStyle" />
    </OlVectorLayer>

    <!-- 编辑 / 区域模板 -->
    <OlTransformInteraction
      v-if="activatedBottomBar === '编辑' || activatedBottomBar === '区域模板'"
    />

    <!-- 警告气泡：activatedPositionOverlayWaring 为告警设备坐标 -->
    <OlOverlay
      v-if="
        waring &&
        activatedPositionOverlayWaring &&
        activatedPositionOverlayWaring.length > 0
      "
      :position="activatedPositionOverlayWaring"
      :auto-pan="true"
      :anchor="[0.5, 1]"
      :offset="[-50, -30]"
    >
      <div
        class="waring-bubble flex flex-col justify-start text-white flex-nowrap"
      >
        <img :src="waringIcon" alt="" class="absolute w-full h-full" />
      </div>
    </OlOverlay>

    <!-- 位置气泡（插槽） -->
    <OlOverlay
      v-if="activatedPositionOverlay.length > 0"
      :position="activatedPositionOverlay"
      :offset="[-175, -255]"
      :auto-pan="true"
    >
      <slot name="overlay" />
    </OlOverlay>
  </OlMap>
</template>
